import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  useContextualNavigate,
  useNavigationCookies,
} from "../../../../hooks/ContextualNavigations";
import {
  AuthState,
  IAuthLoginDTO,
  IAuthService,
} from "../../../../modules/user/models/IAuthService";
import { useIocContext } from "../../../../contexts/ioc/IocContext";
import { Types } from "../../../../ioc/types";
import { getSnackbar } from "../../../../utils/Snackbars";
import AppError from "../../../../utils/appError";
import { routesConfig } from "../../../../config/routes";
import { useParamsContext } from "../../../../contexts/params/ParamsContext";

// Lógica única da autenticação de segurança (MFA), extraída de SecurityAuthDefault — mesma
// validação de url/rules, mesma verificação de permissão (groups inclui rules), mesma máquina
// de estados, mesmos cookies/redirect e guard de location.state. Consumida pela Default e pela
// variante Branded, sem duplicar auth.
export function useSecurityAuth() {
  const navigate = useContextualNavigate();
  const navigateCookies = useNavigationCookies();
  const { params } = useParamsContext();
  const location = useLocation();

  let email = "";
  let password = "";
  if (location.state) {
    ({ email, password } = location.state as IAuthLoginDTO);
  }

  const iocContext = useIocContext();
  const authService = iocContext.serviceContainer.get<IAuthService>(Types.User.IAuthService);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: IAuthLoginDTO) => {
    try {
      const urlString = window.location.href;
      if (urlString.indexOf("?") === -1) {
        throw new Error("Ausência de Parâmetros na Url");
      }

      if (!params.url) {
        throw new Error(
          "Ausência de Parâmetros na Url - link de Redirecionamento (url off)"
        );
      }
      const redirectURL = params.url;

      if (!params.rules) {
        throw new Error(
          "Ausência de Parâmetros na Url - regras do projeto (rules off) "
        );
      }
      const rules = params.rules;

      const loginResponse = await authService.authLogin(values);

      const isAutorized = loginResponse.meta.groups.includes(rules);

      if (loginResponse.state === AuthState.TOO_MANY_ACCESSES) {
        throw new Error("Muitas tentativas de acesso");
      }

      if (loginResponse.state === AuthState.EXHAUSTED_ATTEMPTS) {
        throw new Error(
          "Muitas tentativas de acesso, aguarde um momento e tente novamente"
        );
      }

      if (loginResponse.state === AuthState.MUST_START_CHALLENGE) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        navigate(routesConfig.LOGIN);
        throw new Error("Código expirado, tente novamente!");
      }

      if (loginResponse.state === AuthState.INCORRECT_TOKEN) {
        throw new Error("Codigo Incorreto, Tente Novamente!");
      }

      if (isAutorized) {
        navigateCookies({
          accessToken: loginResponse.AccessToken,
          refreshToken: loginResponse.RefreshToken,
          redirectURL,
        });
      } else {
        enqueueSnackbar(
          getSnackbar({
            message:
              "Você não possui permissões, entre em contato com um administrador!",
            variant: "error",
          })
        );
        navigate(routesConfig.LOGIN);
      }
    } catch (error) {
      const message =
        error instanceof AppError || error instanceof Error
          ? error.message
          : "Erro inesperado, tente novamente.";
      enqueueSnackbar(getSnackbar({ message, variant: "error" }));
    }
  };

  const handleResendCode = async () => {
    try {
      const urlString = window.location.href;
      if (urlString.indexOf("?") === -1) {
        throw new Error("Ausência de Parâmetros na Url");
      }

      if (!params.url) {
        throw new Error(
          "Ausência de Parâmetros na Url - link de Redirecionamento (url off)"
        );
      }

      await authService.authLogin({ email, password });

      enqueueSnackbar(
        getSnackbar({ message: "Código enviado com sucesso", variant: "success" })
      );
    } catch (error) {
      const message =
        error instanceof AppError || error instanceof Error
          ? error.message
          : "Erro inesperado, tente novamente.";
      enqueueSnackbar(getSnackbar({ message, variant: "error" }));
    }
  };

  useEffect(() => {
    if (!location.state || !email || !password) {
      navigate(routesConfig.LOGIN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, location.state, password]);

  return { handleSubmit, handleResendCode, email, password };
}
